import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import TabConfigurar from '../events/tabs/Configurar';
import TabPersonal from '../events/tabs/Personal';
import TabIngreso from '../events/tabs/Ingreso';
import TabVentas from '../events/tabs/Ventas';
import TabLotes from '../events/tabs/Productos';
import TabPagos from '../events/tabs/Pagos';
import GenerarEntradas from '../events/GenerarEntradas';
import { originBase } from '../events/util';
import styles from './Panel.module.css';

// El SOCIO organiza UN evento: ve y edita ese evento como lo haría el dueño, pero
// solo ese. Las cuatro secciones existían desde siempre mostrando "próximamente":
// la pantalla nunca se había construido.
const SECTIONS = [
  { key: 'entradas', label: 'ENTRADAS VENDIDAS' },
  { key: 'rrpp', label: 'RRPP' },
  { key: 'ingreso', label: 'INGRESO' },
  { key: 'lotes', label: 'LOTES' },
  { key: 'pagos', label: 'PAGOS' },
  { key: 'configurar', label: 'CONFIGURAR' },
];

export default function SocioPanel() {
  const { user, logout } = useAuth();
  const api = useApi();
  const [section, setSection] = useState('entradas');
  const [event, setEvent] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Compartir el evento: mismo comportamiento que en el panel del dueño — el menú
  // nativo si el dispositivo lo tiene (en el celular es lo normal), y si no, copiar.
  async function shareEvent() {
    if (!event) return;
    const url = `${originBase()}/e/${event.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: event.name || 'Evento', text: `Entradas: ${event.name || ''}`, url }); return; }
      catch (e) { /* cancelado */ }
    }
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt('Copiá el link del evento:', url); }
  }
  const go = (key) => { setSection(key); setMenuOpen(false); };

  // Dos pasos a propósito: /api/events dice CUÁL es su evento, y /api/events/:id trae
  // el evento COMPLETO. Las pestañas esperan el completo — el del listado no incluye
  // los lotes, así que la pestaña LOTES se veía vacía aunque el evento los tuviera.
  const cargar = () => api.get('/api/events')
    .then(evs => {
      const id = (Array.isArray(evs) ? evs[0] : null)?.id;
      if (!id) { setEvent(null); return null; }
      return api.get(`/api/events/${id}`).then(setEvent);
    })
    .catch(() => {});
  const reload = cargar;

  // El evento se pide a /api/events, que para un socio devuelve exactamente su evento
  // asignado. Antes se leía user.assigned_event_id, pero el login devuelve el campo como
  // assignedEventId: nunca coincidía y el panel mostraba siempre "no tenés evento
  // asignado". Pedirlo al backend además refleja las asignaciones hechas después
  // de que el socio inició sesión, sin depender de lo que quedó grabado en el token.
  useEffect(() => {
    cargar().finally(() => setCargando(false));
  }, [user]); // eslint-disable-line

  return (
    <div className={styles.layout}>
      {menuOpen && <div className={styles.backdrop} onClick={() => setMenuOpen(false)} />}
      <aside className={`${styles.sidebar} ${menuOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarBrand}>
          <img src="/assets/tukipass-icon.png" alt="Tukipass" className={styles.brandMark} style={{ objectFit: 'cover' }} />
          <div>
            <div className={styles.brandName}>TUKIPRO</div>
            <div className={styles.brandRole}>SOCIO</div>
          </div>
        </div>
        {event && (
          <div style={{ padding: '12px 20px', background: 'rgba(139,15,26,0.1)', margin: '0 12px 16px', borderRadius: 'var(--r-sm)', border: '1px solid rgba(139,15,26,0.3)' }}>
            <div style={{ fontSize: 9, letterSpacing: '0.15em', color: 'var(--fg-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Evento asignado</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 11, color: 'var(--white)' }}>{event.name}</div>
          </div>
        )}
        <nav className={styles.nav}>
          {SECTIONS.map(s => (
            <button key={s.key} className={`${styles.navItem} ${section === s.key ? styles.navActive : ''}`} onClick={() => go(s.key)}>
              {s.label}
            </button>
          ))}
        </nav>
        <button className={styles.logoutBtn} onClick={logout}>Cerrar sesión</button>
      </aside>

      <main className={styles.main}>
        <div className={styles.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <button className={styles.menuToggle} onClick={() => setMenuOpen(o => !o)} aria-label="Menú">☰</button>
            <div className={styles.pageTitle}>{SECTIONS.find(s => s.key === section)?.label}</div>
          </div>
          <div className={styles.userChip}>{user?.name}</div>
        </div>
        <div className={styles.content}>
          {cargando ? (
            <div style={{ padding: '80px 0', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>Cargando…</div>
          ) : !event ? (
            <div style={{ padding: '80px 0', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
              No tenés un evento asignado. Contactá al dueño.
            </div>
          ) : (
            <>
              {/* Los dos botones del dueño, sobre SU evento: mandar el link de venta y
                  generar un link de entradas para quien se lo pide por privado. */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: 4 }}>
                <button className="lb-btn lb-btn--ghost" onClick={shareEvent}>{copied ? 'Link copiado ✓' : 'Compartir evento ↗'}</button>
                <button className="lb-btn lb-btn--primary" onClick={() => setGenOpen(true)}>Generar entradas →</button>
              </div>
              {genOpen && <GenerarEntradas event={event} api={api} onClose={() => { setGenOpen(false); reload(); }} />}
              {/* Cuenta de MP donde se acreditan los QR online de SU evento. El backend
                  valida que el evento sea el suyo; no puede tocar el MP del boliche. */}
              {section === 'pagos' && <TabPagos event={event} api={api} />}
              {section === 'configurar' && <TabConfigurar event={event} api={api} reload={reload} />}
              {/* El socio arma el equipo de RRPP de su noche. Solo se le ofrece ese rol:
                  el backend además se lo fuerza, así que la lista no es la protección. */}
              {section === 'rrpp' && <TabPersonal event={event} api={api} reload={reload} inviteRoles={['rrpp']} />}
              {section === 'ingreso' && <TabIngreso event={event} api={api} reload={reload} />}
              {/* Los lotes de SU evento: crear, precio, cupo y estado. Borrar sigue
                  siendo del dueño (el backend lo restringe). */}
              {section === 'lotes' && <TabLotes event={event} api={api} reload={reload} />}
              {section === 'entradas' && <TabVentas event={event} api={api} reload={reload} />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
